import React from 'react';
import { Box, Typography, Chip, LinearProgress, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import { Whatshot as FireIcon } from '@mui/icons-material';
import UserAvatar from './UserAvatar';
import { getTheme, getAccent, getBg, getEffectiveDark, getTextColor, getTextSecondary } from '../../theme/themes';

const LEVEL_NAMES = {
  1: 'Ensiaskel', 2: 'Ahkera Apuri', 3: 'Kotitöiden Sankari',
  4: 'Supersankari', 5: 'Mestari', 6: 'Legenda', 7: 'Kotitöiden Jumala',
};
const LEVEL_THRESHOLDS = [0, 50, 150, 350, 700, 1200, 2000];

const ChildProfile = ({
  child,
  variant = 'medium',
  isDark = false,
  onClick,
  showName = true,
  showStats = false,
  children: extraContent,
}) => {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const accent = getAccent(child);
  const bg = getBg(child, isDark);
  const effectiveDark = getEffectiveDark(child, isDark);
  const textColor = getTextColor(child, isDark);
  const textSecondary = getTextSecondary(child, isDark);

  const level = child?.level || 1;
  const totalXp = child?.total_xp || child?.totalXp || 0;
  const currentLevelXp = LEVEL_THRESHOLDS[level - 1] || 0;
  const nextLevelXp = LEVEL_THRESHOLDS[level] || 999999;
  const xpProgress = nextLevelXp > currentLevelXp
    ? Math.min(100, Math.round(((totalXp - currentLevelXp) / (nextLevelXp - currentLevelXp)) * 100))
    : 100;

  // XL variant - ProfiiliPage hero style
  if (variant === 'xl') {
    const theme = getTheme(child);
    const profileHeader = theme?.profileHeader;

    // Responsive header sizing
    const headerHeight = profileHeader?.height
      ? (typeof profileHeader.height === 'object'
          ? (isMobile ? profileHeader.height.mobile : profileHeader.height.tablet)
          : profileHeader.height)
      : 140;
    const headerPaddingTop = profileHeader?.paddingTop
      ? (typeof profileHeader.paddingTop === 'object'
          ? (isMobile ? profileHeader.paddingTop.mobile : profileHeader.paddingTop.tablet)
          : profileHeader.paddingTop)
      : headerHeight * 0.4;
    const cardPaddingTop = profileHeader?.cardPaddingTop || (profileHeader ? 3 : 2);

    return (
      <Box
        onClick={onClick}
        sx={{
          position: 'relative',
          cursor: onClick ? 'pointer' : 'default',
          // Allow header images to overflow
          overflow: 'visible',
          // Add top padding when header exists to make room for overflow
          pt: profileHeader ? `${headerPaddingTop}px` : 0,
        }}
      >
        {/* Background layer - goes BEHIND the card */}
        {profileHeader?.bgSrc && (
          <Box
            component="img"
            src={profileHeader.bgSrc}
            alt=""
            sx={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '120%',
              height: headerHeight,
              objectFit: 'contain',
              objectPosition: 'center top',
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
        )}

        {/* Profile card content */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            background: profileHeader?.cardGradient || `linear-gradient(160deg, ${accent}33 0%, transparent 60%)`,
            px: 2, pt: typeof cardPaddingTop === 'number' && cardPaddingTop > 10 ? `${cardPaddingTop}px` : cardPaddingTop, pb: 2, borderRadius: '16px', mb: 3,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
          }}
        >
          <UserAvatar
            src={child?.avatar_url}
            frame={child?.avatar_frame}
            size={160}
            frameScale={1.50}
            bgcolor={accent}
            sx={{
              fontSize: '4rem',
              fontWeight: 900,
              border: `4px solid ${effectiveDark ? 'rgba(255,255,255,0.15)' : '#fff'}`,
              boxShadow: `0 6px 24px ${accent}55`,
            }}
          >
            {child?.name?.charAt(0)}
          </UserAvatar>
          {showName && (
            <Typography variant="h5" fontWeight={900} sx={{ color: profileHeader?.cardGradient ? '#fff' : accent, mt: 1 }}>
              {child?.name}
            </Typography>
          )}
          {showStats && (
            <>
              <Chip
                label={`Taso ${level}: ${LEVEL_NAMES[level] || ''}`}
                sx={{ bgcolor: profileHeader?.cardGradient ? 'rgba(255,255,255,0.2)' : `${accent}22`, color: profileHeader?.cardGradient ? '#fff' : accent, fontWeight: 700 }}
              />
              <Box sx={{ width: '100%', maxWidth: 200, mt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: profileHeader?.cardGradient ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>{totalXp} XP</Typography>
                  <Typography variant="caption" sx={{ color: profileHeader?.cardGradient ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}>{nextLevelXp}</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={xpProgress}
                  sx={{
                    height: 6, borderRadius: 50,
                    bgcolor: profileHeader?.cardGradient ? 'rgba(255,255,255,0.2)' : (effectiveDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'),
                    '& .MuiLinearProgress-bar': { bgcolor: profileHeader?.cardGradient ? '#fff' : accent },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                {(child?.current_streak || child?.currentStreak) >= 3 && (
                  <Chip
                    icon={<FireIcon sx={{ color: '#fff !important' }} />}
                    label={`${child?.current_streak || child?.currentStreak} pv`}
                    size="small"
                    sx={{ bgcolor: '#ff6d00', color: '#fff', fontWeight: 700 }}
                  />
                )}
                <Chip
                  label={`🪙 ${child?.coins || 0}`}
                  size="small"
                  sx={{ bgcolor: '#fff8e1', color: '#f57f17', fontWeight: 700 }}
                />
              </Box>
            </>
          )}
          {extraContent}
        </Box>

        {/* Foreground layer - goes IN FRONT of the card */}
        {profileHeader?.fgSrc && (
          <Box
            component="img"
            src={profileHeader.fgSrc}
            alt=""
            sx={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '120%',
              height: headerHeight,
              objectFit: 'contain',
              objectPosition: 'center top',
              pointerEvents: 'none',
              zIndex: 2,
            }}
          />
        )}

        {/* Legacy single src support */}
        {profileHeader?.src && !profileHeader.fgSrc && !profileHeader.bgSrc && (
          <Box
            component="img"
            src={profileHeader.src}
            alt=""
            sx={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '120%',
              height: headerHeight,
              objectFit: 'contain',
              objectPosition: 'center top',
              pointerEvents: 'none',
              zIndex: 2,
            }}
          />
        )}
      </Box>
    );
  }

  // Medium-tall variant - Login dialogs, PIN entry
  if (variant === 'medium-tall') {
    return (
      <Box
        onClick={onClick}
        sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75,
          cursor: onClick ? 'pointer' : 'default',
          p: 1.5, borderRadius: '16px', bgcolor: bg,
          border: `2px solid ${accent}22`,
          transition: 'transform 0.15s',
          '&:hover': onClick ? { transform: 'scale(1.05)' } : {},
          minWidth: 80,
        }}
      >
        <UserAvatar
          src={child?.avatar_url}
          frame={child?.avatar_frame}
          size={56}
          bgcolor={accent}
          sx={{ fontWeight: 900, fontSize: '1.4rem' }}
        >
          {child?.name?.charAt(0)}
        </UserAvatar>
        {showName && (
          <Typography variant="body2" fontWeight={700} sx={{ color: textColor }}>
            {child?.name}
          </Typography>
        )}
        {extraContent}
      </Box>
    );
  }

  // Medium variant - KioskDashboard, CalendarPage headers
  if (variant === 'medium') {
    return (
      <Box
        onClick={onClick}
        sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
          cursor: onClick ? 'pointer' : 'default',
        }}
      >
        <UserAvatar
          src={child?.avatar_url}
          frame={child?.avatar_frame}
          size={80}
          frameScale={1.50}
          bgcolor={accent}
          sx={{
            fontSize: '2rem', fontWeight: 900,
            border: `3px solid ${effectiveDark ? 'rgba(255,255,255,0.15)' : '#fff'}`,
            boxShadow: `0 4px 16px ${accent}44`,
          }}
        >
          {child?.name?.charAt(0)}
        </UserAvatar>
        {showName && (
          <Typography variant="h6" fontWeight={900} sx={{ color: accent }}>
            {child?.name}
          </Typography>
        )}
        {extraContent}
      </Box>
    );
  }

  // Small variant - Lists, compact displays
  if (variant === 'small') {
    return (
      <Box
        onClick={onClick}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          cursor: onClick ? 'pointer' : 'default',
        }}
      >
        <UserAvatar
          src={child?.avatar_url}
          frame={child?.avatar_frame}
          size={40}
          bgcolor={accent}
          sx={{
            fontSize: '1rem', fontWeight: 900,
            border: `2px solid ${effectiveDark ? 'rgba(255,255,255,0.1)' : '#fff'}`,
            boxShadow: `0 2px 8px ${accent}33`,
          }}
        >
          {child?.name?.charAt(0)}
        </UserAvatar>
        {showName && (
          <Typography variant="body2" fontWeight={700} sx={{ color: textColor }}>
            {child?.name}
          </Typography>
        )}
        {extraContent}
      </Box>
    );
  }

  // Button variant - Filter buttons, tabs
  if (variant === 'button') {
    return (
      <Box
        onClick={onClick}
        sx={{
          position: 'relative',
          display: 'inline-flex', alignItems: 'center', gap: 2,
          cursor: onClick ? 'pointer' : 'default',
          bgcolor: bg,
          border: `2px solid ${accent}`,
          borderRadius: 20,
          pl: 5.7, pr: 2, py: 0.75, 
          transition: 'all 0.15s',
          '&:hover': onClick ? { bgcolor: `${accent}22` } : {},
        }}
      >
        <Box sx={{ position: 'absolute', left: -3, top: '50%', transform: 'translateY(-50%)' }}>
          <UserAvatar
            src={child?.avatar_url}
            frame={child?.avatar_frame}
            size={32}
            bgcolor={accent}
            sx={{
              fontSize: '0.85rem', fontWeight: 900,
              border: `2px solid ${accent}`,
            }}
          >
            {child?.name?.charAt(0)}
          </UserAvatar>
        </Box>
        {showName && (
          <Typography variant="body2" fontWeight={700} sx={{ color: textColor }}>
            {child?.name}
          </Typography>
        )}
        {child?.displaySuffix && (
          <Typography variant="body2" fontWeight={700} sx={{ color: accent }}>
            {child.displaySuffix}
          </Typography>
        )}
        {extraContent}
      </Box>
    );
  }

  // Fallback to medium
  return null;
};

export default ChildProfile;
